"use client";

import { getTowerLevel } from "@/lib/tower";
import { PublicUser, Team, TeamStatistics, Tournament } from "@/types";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { TeamCard } from "./TeamCard";

interface TowerLevelProps {
  level: number;
  teams: Team[];
  players: PublicUser[];
  currentUserTeam: Team | null;
  tournament: Tournament;
  teamStatsMap: Map<string, TeamStatistics>;
  onTeamClick: (team: Team) => void;
}

export function TowerLevel({
  level,
  teams,
  players,
  currentUserTeam,
  tournament,
  teamStatsMap,
  onTeamClick,
}: TowerLevelProps) {
  const t = useTranslations();

  // Pre-compute players by team ID to avoid O(n²) filtering (fix #11)
  // First create a player lookup map O(n), then build team->players map O(teams × playersPerTeam)
  const playersByTeamId = useMemo(() => {
    // Create lookup map for O(1) player access
    const playerLookup = new Map<string, PublicUser>();
    for (const player of players) {
      playerLookup.set(player.id, player);
    }

    // Build team -> players map using O(1) lookups
    const map = new Map<string, PublicUser[]>();
    for (const team of teams) {
      const teamPlayers: PublicUser[] = [];
      for (const playerId of team.playerIds) {
        const player = playerLookup.get(playerId);
        if (player) {
          teamPlayers.push(player);
        }
      }
      map.set(team.id, teamPlayers);
    }
    return map;
  }, [teams, players]);

  if (!tournament) {
    return null;
  }

  const getLevelLabel = (levelIndex: number): string => {
    if (levelIndex === 0) return t("tower.levelTop");
    return t("tower.level", { level: levelIndex + 1 });
  };

  return (
    <div
      className="flex flex-col items-center gap-3 sm:gap-6"
      style={{ scrollSnapAlign: "center" }}
    >
      {/* Level label with decorative lines */}
      <div className="mb-1 sm:mb-4 flex items-center gap-2 sm:gap-3">
        <div className="h-[1px] w-[30px] sm:w-[60px] md:w-[250px] bg-gradient-to-r from-transparent to-gold-dark" />
        <span className="font-cinzel text-[0.65rem] sm:text-[0.75rem] md:text-[0.85rem] font-bold uppercase tracking-[0.08em] sm:tracking-[0.12em] text-gold whitespace-nowrap">
          {getLevelLabel(level)}
        </span>
        <div className="h-[1px] w-[30px] sm:w-[60px] md:w-[250px] bg-gradient-to-l from-transparent to-gold-dark" />
      </div>
      {/* Team cards grid - 2 per row on mobile with wider cards, flexible on larger screens */}
      <div className="flex flex-wrap justify-center gap-2.5 sm:gap-[14px] md:gap-[18px] xl:gap-[22px] w-full px-2 sm:px-0 sm:max-w-none">
        {teams.map((team) => {
          const teamPlayers = playersByTeamId.get(team.id) ?? [];
          const isCurrentTeam = currentUserTeam?.id === team.id;
          const teamStats = teamStatsMap.get(team.id);

          // Calculate level from calculatedPosition if available
          const currentUserLevel = currentUserTeam?.calculatedPosition
            ? getTowerLevel(
                currentUserTeam.calculatedPosition,
                tournament.towerTeamsPerRow,
                tournament.towerTopRowTeamCount,
              )
            : 999;

          const teamLevel = team.calculatedPosition
            ? getTowerLevel(
                team.calculatedPosition,
                tournament.towerTeamsPerRow,
                tournament.towerTopRowTeamCount,
              )
            : level;

          const isAboveMe = currentUserTeam
            ? teamLevel < currentUserLevel
            : false;

          return (
            <TeamCard
              key={team.id}
              team={team}
              players={teamPlayers}
              currentUserTeam={currentUserTeam}
              isCurrentTeam={isCurrentTeam}
              isAboveMe={isAboveMe}
              teamStats={teamStats}
              onTeamClick={onTeamClick}
            />
          );
        })}
      </div>
    </div>
  );
}
